import { ComponentType, createElement, ReactNode } from "react";
import { LazyRouteFunction, RouteObject } from "react-router";

export type TRouteGuard = ComponentType<{ children: ReactNode }>;

interface ILazyRouteWithGuardsProps<TModule extends Record<string, unknown>> {
  importer: () => Promise<TModule>;
  componentName: keyof TModule;
  guards?: TRouteGuard[];
}

/**
 * Loads a component dynamically using the provided importer function.
 * @param props  Props containing the importer function and component name.
 * @returns
 */
const loadComponent = async <TModule extends Record<string, unknown>>(
  props: ILazyRouteWithGuardsProps<TModule>,
): Promise<ComponentType<{ children?: ReactNode }>> => {
  const module = await props.importer();
  const component = module[props.componentName];

  if (!component) {
    throw new Error(`Component '${String(props.componentName)}' was not found in lazy-loaded module.`);
  }

  return component as ComponentType<{ children?: ReactNode }>;
};

/**
 * Wraps a component with multiple guard components hierarchically in the form of:
 * <Guard1>
 *   <Guard2>
 *     ...
 *      <GuardN>
 *       <Component />
 *     </GuardN>
 *   ...
 *  </Guard2>
 * </Guard1>
 * @param component The main component to be wrapped.
 * @param guards An array of guard components to wrap around the main component.
 * @returns A new component type that represents the main component wrapped with the specified guards.
 */
function wrapComponentWithGuards(component: ComponentType<{ children?: ReactNode }>, ...guards: TRouteGuard[]) {
  return guards.reduceRight<ComponentType<{ children?: ReactNode }>>(
    (guardedComponent, guard) =>
      ({ children }: { children?: ReactNode }) => {
        const guardedElement = createElement(guardedComponent, null, children);

        return createElement(guard, null, guardedElement);
      },
    ({ children }: { children?: ReactNode }) => createElement(component, null, children),
  );
}

/**
 * Type-safe helper to create a lazy-loaded route component with guards.
 * @param routeProps The properties required to create the lazy route with guards.
 * @returns A LazyRouteFunction that loads the component wrapped with the specified guards.
 */
export function lazyRoute<TModule extends Record<string, unknown>>(
  routeProps: ILazyRouteWithGuardsProps<TModule>,
): LazyRouteFunction<RouteObject> {
  return async () => {
    const Component = await loadComponent(routeProps);
    const wrappedComponentWithGuards = wrapComponentWithGuards(Component, ...(routeProps.guards ?? []));

    return { Component: wrappedComponentWithGuards };
  };
}
